import { AlarmingStore } from '../store/AlarmingStore';

export const ALARM_LEVEL = {
    OUT_OF_BOUNDS_LOW: 1,
    GOOD: 2,
    WARNING: 3,
    ALARM: 4,
    OUT_OF_BOUNDS_HIGH: 5,
};

export const ALARM_STATE_BY_LEVEL = {
    [ALARM_LEVEL.OUT_OF_BOUNDS_LOW]: 'undefined',
    [ALARM_LEVEL.GOOD]: 'good',
    [ALARM_LEVEL.WARNING]: 'warning',
    [ALARM_LEVEL.ALARM]: 'alarm',
    [ALARM_LEVEL.OUT_OF_BOUNDS_HIGH]: 'undefined',
};

export const DEFAULT_CLASSIFICATION_TEMPLATE = {
    templateEntries: [
        {
            id: 'c47fa8d2-4a2d-4fd0-8d18-d98eb41441d6',
            name: 'level_1',
            iconBackGroundColor: '#5930a6',
            settings: 'outerlimit',
        },
        {
            id: 'f005b307-8f27-4f54-bd8f-3f67b3e9912d',
            name: 'level_2',
            iconBackGroundColor: '#a9e382',
            settings: null,
        },
        {
            id: '942771a4-fc4a-4801-bae3-a3b8837bb31d',
            name: 'level_3',
            iconBackGroundColor: '#ffa857',
            settings: null,
        },
        {
            id: '3784c0bb-7fee-433c-ab80-dfa40856b57a',
            name: 'level_4',
            iconBackGroundColor: '#f96c81',
            settings: null,
        },
        {
            id: 'a5860581-fd5d-4071-87d9-a0a55a6c1f2e',
            name: 'level_5',
            iconBackGroundColor: '#ffffff',
            settings: 'outerlimit',
        },
    ],
};

/**
 * Get alarm status for a channel
 * @param {String} channelId
 * @returns {Promise<Array>} Array of alarm status entries
 */
export async function getAlarmStatus(channelId) {
    return AlarmingStore.getAlarmStatus(channelId);
}

/**
 * Get alarm configurations for a channel
 * @param {String} channelId
 * @returns {Promise<Array>} Array of alarm config objects
 */
export async function getAlarmConfigs(channelId) {
    return AlarmingStore.getAlarmConfigurations(channelId);
}

/**
 * Get the classification template (currently returns hardcoded default)
 * @returns {Object} The classification template
 */
export function getClassificationTemplate() {
    return DEFAULT_CLASSIFICATION_TEMPLATE;
}

/**
 * Fetch all alarming data for a channel in one call
 * @param {String} channelId
 * @param {Object} [options]
 * @returns {Promise<{alarmStatus: Array, alarmConfigs: Array, classificationTemplate: Object}>}
 */
export async function getAlarmData(channelId) {
    const configsFn = getAlarmConfigs(channelId);

    const [alarmStatus, alarmConfigs] = await Promise.all([getAlarmStatus(channelId), configsFn]);

    return {
        alarmStatus,
        alarmConfigs,
        classificationTemplate: getClassificationTemplate(),
    };
}

/**
 * Resolve the alarm state string for a given level
 * @param {Number} level The alarm level (1-5)
 * @returns {String} The state string
 */
export function resolveAlarmState(level) {
    return ALARM_STATE_BY_LEVEL[level] || 'undefined';
}

/**
 * Find alarm status for a specific timeseries from a status list
 * @param {Array} alarmStatusList
 * @param {String} tsId
 * @returns {{state: String, level: Number|null, alarm: Object|null}}
 */
export function getStatusForTimeseries(alarmStatusList, tsId) {
    const alarm = alarmStatusList?.find((s) => s.tsId === tsId);
    if (!alarm) return { state: 'undefined', level: null, alarm: null };
    return {
        state: resolveAlarmState(alarm.level),
        level: alarm.level,
        alarm,
    };
}

/**
 * Find alarm config for a specific timeseries from a config list
 * @param {Array} alarmConfigs
 * @param {String} tsId
 * @returns {Object|null}
 */
export function getConfigForTimeseries(alarmConfigs, tsId) {
    return alarmConfigs?.find((c) => c.tsId === tsId) || null;
}
